import React, { Fragment, useState } from 'react';
import Header from '../header';
import Logo from '../../assets/logo-sm1.svg';
import cx from 'classnames';
import { clearLogoutError, unimpersonate, logout } from '../../slices/userSlice';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import {
  BellIcon,
  ChevronDownIcon,
  MenuIcon,
  XIcon,
  ChatAltIcon,
} from '@heroicons/react/outline';
import { Role, powerBiLink } from '../../common/constants';
import DisplayFor, { IsAllowed } from '../displayFor';
import { Link, NavLink, useHistory } from 'react-router-dom';
import PageSpinner from '../pageSpinner';
import ErrorModal from '../errorModal';
import { clearLocalStorage, hasValue } from '../../common/utils';
import { ExclamationCircleIcon } from '@heroicons/react/solid';
import { NavigationItem } from './types/navigationItem';

interface Props {
  children?: React.ReactElement;
  isLoading?: boolean;
  isScrollable?: boolean;
}

const Layout = ({ children, isLoading = false, isScrollable = true }: Props) => {
  const dispatch = useAppDispatch();
  const name = useAppSelector((s) => s.user.firstName + ' ' + s.user.lastName);
  const email = useAppSelector((s) => s.user.email);
  const unreadMessagesCount = useAppSelector(s => s.user.unreadMessagesCount);
  const isInImpersonationMode = useAppSelector((s) => s.user.id && s.user.loggedInUserId && s.user.id !== s.user.loggedInUserId);
  const hasLogoutError = useAppSelector((s) => s.user.hasLogoutError === true);
  const isLoggingOut = useAppSelector((s) => s.user.loggingOut === true);
  const userRoles = useAppSelector(s => s.user.roles);
  const history = useHistory();

  const handleLogout = () => {
    clearLocalStorage();
    dispatch(logout());
  };

  const unimpersonateUser = () => {
    clearLocalStorage();
    dispatch(unimpersonate());
  };

  const handleClearLogoutErrors = () => {
    dispatch(clearLogoutError());
  };

  /**
   * District Menu Children
   * Any time the location changes, we check to see if the path starts with /admin/districts
   * If so, then we get the district id and add a new menu item. Otherwise, we just return an empty array
   * Further, we memoize the variable instead of using useEffect, as this will only update the value if there are
   * changes, resulting in better performance.
   */
  /*const districtsChildren = useMemo(() => {
    const path = location.pathname.replace('/admin/districts/', '').replace('/rates/approve', '');

    if (location.pathname.startsWith('/admin/districts/') && path !== '' && (Number.isInteger(Number(path) || path.includes('rates/approve')))) {
      return [
        {
          name: 'Approve Rates',
          to: `/admin/districts/${path}/rates/approve`,
          roles: [Role.Director, Role.BillingSpecialist]
        },
      ];
    }

    return [];
  }, [location]);*/

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [navigation, setNavigation] = useState<NavigationItem[]>([
    //Districts and therapists
    {
      name: 'Districts',
      to: '/admin/districts',
      roles: [Role.Admin, Role.Director, Role.BillingSpecialist, Role.Accounting, Role.Operations],
      children: [
        {
          name: 'Districts',
          to: '/admin/districts/',
          roles: [Role.Admin, Role.Director, Role.BillingSpecialist, Role.Accounting, Role.Operations]
        },
        {
          name: 'Approve Rates',
          to: `/admin/districts/rates/approve`,
          roles: [Role.Director]
        },
      ]
    },
    {
      name: 'Therapists',
      to: '/admin/therapists',
      roles: [Role.BillingSpecialist, Role.Director, Role.Operations, Role.Admin],
      children: [
        {
          name: 'Therapists',
          to: '/admin/therapists',
          roles: [Role.Therapist, Role.BillingSpecialist, Role.Director, Role.Operations, Role.Admin, Role.Accounting]
        },
        {
          name: 'Approve Changes',
          to: '/admin/therapists/approve',
          roles: [Role.BillingSpecialist, Role.Director]
        },
        {
          name: 'Unlock Timesheet Requests',
          to: '/admin/therapists/unlockTimesheets',
          roles: [Role.BillingSpecialist, Role.Director]
        },
      ]
    },

    //Timesheets
    {
      name: 'Timesheets',
      to: '/timesheets',
      roles: [Role.Therapist, Role.BillingSpecialist, Role.Admin, Role.Accounting, Role.Director],
      children: [
        {
          name: 'Timesheets',
          to: '/timesheets',
          roles: [Role.Therapist, Role.Accounting],
          exceptRoles: [Role.SuperUser]
        },
        {
          name: 'Proofing',
          to: '/proofing',
          roles: [Role.BillingSpecialist]
        },
        {
          name: 'Timesheet Summary',
          to: '/proofing/summary',
          roles: [Role.BillingSpecialist]
        },
        {
          name: 'TimeEntry Transfer',
          to: '/proofing/transfer',
          roles: [Role.BillingSpecialist]
        },
        {
          name: 'Proofing Rules',
          to: '/admin/proofing',
          roles: [Role.Admin]
        },
        {
          name: 'Timesheet Search',
          to: '/admin/timesheetSearch',
          roles: [Role.Admin, Role.Director, Role.BillingSpecialist, Role.Accounting]
        },
      ]
    },

    //Caseloads
    {
      name: 'Caseload',
      to: '/caseload',
      roles: [Role.Therapist, Role.Admin, Role.BillingSpecialist, Role.Director],
      children: [
        {
          name: 'Caseload',
          to: '/caseload',
          roles: [Role.Therapist],
          exceptRoles: [Role.SuperUser]
        },
        {
          name: 'Caseload Approve',
          to: '/caseload/approve',
          roles: [Role.BillingSpecialist]
        },
        {
          name: 'Caseload Transfer',
          to: '/caseloadTransfer',
          roles: [Role.BillingSpecialist]
        },
        {
          name: 'Student Merge',
          to: '/caseload/duplicates',
          roles: [Role.Admin, Role.BillingSpecialist]
        },
        {
          name: 'Caseload Search',
          to: '/caseload/searchCaseloads',
          roles: [Role.Admin, Role.BillingSpecialist, Role.Director]
        },
         {
          name: 'Manual Merge',
          to: '/caseload/manualmerge',
          roles: [Role.Admin, Role.BillingSpecialist, Role.Director]
        },
      ]
    },

    //Accounting 
    {
      name: 'Accounting',
      to: '/accounting',
      roles: [Role.Admin, Role.Director, Role.BillingSpecialist, Role.Accounting],
      children: [
        {
          name: 'Invoice Review',
          to: '/accounting/invoice/review',
          roles: [Role.Admin, Role.Director, Role.BillingSpecialist]
        },
        {
          name: 'Invoice Export',
          to: '/accounting/invoice/exports',
          roles: []
        },
        {
          name: 'Bill Creator',
          to: '/accounting/bill',
          roles: []
        },
        {
          name: 'Bill Exports',
          to: '/accounting/bill/exports',
          roles: [],
          children: [
            {
              name: 'Bill Export Details',
              to: '/accounting/bill/exports/details',
              roles: []
            },
          ]
        },
      ]
    },

    //Admin
    {
      name: 'Admin',
      to: '/admin',
      roles: [Role.Admin, Role.Operations, Role.BillingSpecialist],
      children: [
        {
          name: 'Disciplines',
          to: '/admin/disciplines',
          roles: [Role.Admin]
        },
        {
          name: 'Divisions',
          to: '/admin/divisions',
          roles: [Role.Admin]
        },
        {
          name: 'Home Districts',
          to: '/admin/homeDistricts',
          roles: [Role.Admin, Role.BillingSpecialist, Role.Operations]
        },
        {
          name: 'Notifications',
          to: '/admin/notifications',
          roles: [Role.Admin, Role.BillingSpecialist],
        },
        {
          name: 'Task Types',
          to: '/admin/task-types',
          roles: [Role.Admin]
        },
        {
          name: 'ESYs',
          to: '/admin/esys',
          roles: [Role.Admin]
        },
        {
          name: 'Placements',
          to: '/admin/placements',
          roles: [Role.Admin]
        },
        {
          name: 'Users',
          to: '/admin/users',
          roles: [Role.Admin, Role.Operations]
        },
        {
          name: 'Discipline Group Rules',
          to: '/admin/disciplineGroupRules',
          roles: [Role.Admin]
        },
      ]
    },

    //Reports
    {
      name: 'Reports',
      to: '/reports',
      roles: [Role.Admin, Role.BillingSpecialist, Role.Operations, Role.Accounting, Role.Director],
      children: [
        {
          name: 'PowerBI',
          to: powerBiLink,
          roles: [Role.Admin, Role.BillingSpecialist, Role.Operations, Role.Accounting, Role.Director]
        },
        {
          name: 'Cost Per Child',
          to: '/reports/costperchild',
          roles: []
        }
      ]
    }
  ]);

  const userNavigation = [
    { name: 'My Profile', to: '/my-profile' },
    { name: 'Change Password', to: '/change-password' },
    { name: 'Sign out', action: handleLogout },
  ];

  if (isInImpersonationMode) {
    userNavigation.splice(1, 0, { name: 'Exit Impersonation', action: unimpersonateUser })
  }

  return (
    <Fragment>
      <ErrorModal
        open={hasLogoutError}
        title='Unable to logout'
        description='We had some trouble attempting to log you out of your account. Please close this modal, and try again.'
        onClose={handleClearLogoutErrors}
      />
      <PageSpinner show={isLoading || isLoggingOut} />
      <div className={`w-full h-full bg-ptsbg`} style={{ maxHeight: `${isScrollable ? '100vh' : '100%'}` }}>
        <Disclosure as='nav' className='bg-ptsgreen-dark print:hidden'>
          {({ open }) => (
            <>
              <div className='px-4 mx-auto max-w-7xl sm:px-6 lg:px-8'>
                <div className='flex items-center justify-between h-16'>
                  <div className='flex items-center'>
                    <div className='flex-shrink-0'>
                      <img className='h-10' src={Logo} alt='Workflow' onClick={() => history.push('/')} style={{ cursor: 'pointer' }} />
                    </div>
                    <div className='hidden md:block'>
                      <div className='flex items-baseline ml-10 space-x-4'>
                        {navigation.map((item, index) => {
                          return IsAllowed(item.roles, true, userRoles) ?
                            <Menu
                              as='div'
                              key={`menu-${item.name}-${index}`}
                              className='relative inline-block text-left'
                            >
                              <DisplayFor
                                key={item.name}
                                roles={item.roles}
                                allowOnEmpty={true}
                              >
                                <>
                                  <Menu.Button
                                    className='px-3 py-2 font-medium no-underline bg-transparent text-white border-none rounded'
                                    style={{ fontSize: '13px' }}
                                  >
                                    {!hasValue(item.children) && (
                                      <NavLink
                                        to={item.to}
                                        exact
                                        activeClassName='text-gray-300'
                                        className='font-medium no-underline bg-transparent text-white border-none rounded'
                                        style={{ fontSize: '13px' }}
                                      >
                                        {item.name}
                                      </NavLink>
                                    )}
                                    {hasValue(item.children) && (
                                      <span className="flex items-center">{item.name} <ChevronDownIcon className='flex-shrink-0 w-5 h-5 ml-1 -mr-1' aria-hidden='true' /></span>
                                    )}
                                  </Menu.Button>
                                  {hasValue(item.children) && (
                                    <Menu.Items className='absolute z-50 w-40 mt-2 origin-top-right bg-white rounded-md shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none'>
                                      {item.children?.map((child) => (
                                        <DisplayFor
                                          key={child.name}
                                          roles={child.roles}
                                          allowOnEmpty={true}
                                          exceptRoles={child.exceptRoles}
                                        >
                                          <Menu.Item key={item.name}>
                                            {({ active }) => (
                                              <NavLink
                                                to={child.to.toLowerCase().includes('http') ? { pathname: child.to } : child.to}
                                                target={child.to.toLowerCase().includes('http') ? '_blank' : ''}
                                                exact
                                                activeClassName='bg-gray-200 text-white'
                                                className={`block px-4 py-2 text-sm no-underline text-gray-700 hover:bg-gray-200 rounded`}
                                              >
                                                {child.name}
                                              </NavLink>
                                            )}
                                          </Menu.Item>
                                        </DisplayFor>
                                      ))}
                                    </Menu.Items>
                                  )}
                                </>
                              </DisplayFor>
                            </Menu>

                            : <Fragment key={`menu-${item.name}-${index}`}></Fragment>
                        })}
                      </div>
                    </div>
                  </div>
                  <div className='hidden md:block'>
                    <div className='flex items-center ml-4 md:ml-6'>
                      <NavLink to='/messages' exact>
                        <button
                          type='button'
                          className='flex-shrink-0 p-1 ml-auto mr-3 text-white bg-ptsgreen-dark rounded-full hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-ptsgreen-dark focus:ring-white'
                        >
                          <span className='sr-only'>Message Center</span>
                          {unreadMessagesCount === 0 && <ChatAltIcon className='w-6 h-6' aria-hidden='true' />}
                          {unreadMessagesCount! > 0 && <div style={{ display: 'flex' }}><div style={{ display: 'flex', justifyContent: 'center' }}><ExclamationCircleIcon className='w-4 h-4 cursor-pointer' style={{ position: 'absolute' }} aria-hidden='true' color='red' /></div> <ChatAltIcon className='w-6 h-6' aria-hidden='true' /></div>}
                        </button>
                      </NavLink>
                      <button
                        type='button'
                        className='flex-shrink-0 p-1 ml-auto mr-3 text-white bg-ptsgreen-dark rounded-full hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-ptsgreen-dark focus:ring-white'
                      >
                        <span className='sr-only'>View notifications</span>
                        <BellIcon className='w-6 h-6' aria-hidden='true' />
                      </button>
                      {/* Profile dropdown */}
                      <Menu
                        as='div'
                        className='relative inline-block text-left'
                      >
                        <div>
                          <Menu.Button
                            className={cx({
                              'group flex justify-center items-center hover:bg-ptshover hover:text-black px-3 py-2 rounded-md text-sm font-medium bg-ptsnavmenu text-white': true,
                              'bg-impersonate-bg text-impersonate-text font-bold': isInImpersonationMode,
                            })
                            }>
                            {name}
                            <ChevronDownIcon
                              className={cx('flex-shrink-0 -mr-1 ml-1 h-5 w-5 group-hover:text-gray-500', (isInImpersonationMode ? 'text-impersonate-text' : 'text-gray-400'))}
                              aria-hidden='true'
                            />
                          </Menu.Button>
                        </div>

                        <Transition
                          as={Fragment}
                          enter='transition ease-out duration-100'
                          enterFrom='transform opacity-0 scale-95'
                          enterTo='transform opacity-100 scale-100'
                          leave='transition ease-in duration-75'
                          leaveFrom='transform opacity-100 scale-100'
                          leaveTo='transform opacity-0 scale-95'
                        >
                          <Menu.Items className='absolute right-0 z-50 w-40 mt-2 origin-top-right bg-white rounded-md shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none'>
                            <div className='m-1'>
                              {userNavigation.map((item) => (
                                <Menu.Item key={item.name}>
                                  {({ active }) => (
                                    <React.Fragment>
                                      {item.action && (
                                        <button
                                          onClick={item.action}
                                          className={cx(
                                            active
                                              ? 'bg-gray-100'
                                              : 'bg-transparent',
                                            'block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded'
                                          )}
                                        >
                                          {item.name}
                                        </button>
                                      )}

                                      {!item.action && (
                                        <Link
                                          to={item.to}
                                          className={cx(
                                            active ? 'bg-gray-100' : '',
                                            'block px-4 py-2 text-sm no-underline text-gray-700 hover:bg-gray-200 rounded'
                                          )}
                                        >
                                          {item.name}
                                        </Link>
                                      )}
                                    </React.Fragment>
                                  )}
                                </Menu.Item>
                              ))}
                            </div>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    </div>
                  </div>
                  <div className='flex -mr-2 md:hidden'>
                    {/* Mobile menu button */}
                    <Disclosure.Button className='inline-flex items-center justify-center p-2 text-white bg-ptsgreen-dark rounded-md hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-ptsgreen-dark focus:ring-white'>
                      <span className='sr-only'>Open main menu</span>
                      {open ? (
                        <XIcon className='block w-6 h-6' aria-hidden='true' />
                      ) : (
                        <MenuIcon
                          className='block w-6 h-6'
                          aria-hidden='true'
                        />
                      )}
                    </Disclosure.Button>
                  </div>
                </div>
              </div>

              <Disclosure.Panel className='md:hidden'>
                <div className='px-2 pt-2 pb-3 space-y-1 sm:px-3'>
                  {navigation.map((item) => (
                    <DisplayFor
                      key={item.name}
                      roles={item.roles}
                      allowOnEmpty={true}
                    >
                      <NavLink
                        to={item.to}
                        exact
                        activeClassName='text-gray-300'
                        className='block px-3 py-2 font-medium no-underline bg-transparent text-white border-none rounded'
                        style={{ fontSize: '13px' }}
                      >
                        {item.name}
                      </NavLink>
                    </DisplayFor>
                  ))}
                </div>
                <div className='pt-4 pb-3 border-t border-gray-700'>
                  <div className='flex justify-between px-5'>
                    <div>
                      <div className='mb-1 text-base font-medium leading-none text-white'>
                        {name}
                      </div>
                      <div className='text-sm font-medium leading-none text-gray-400'>
                        {email}
                      </div>
                    </div>
                    <div>
                      <NavLink to='/messages' exact>
                        <button
                          type='button'
                          className='flex-shrink-0 p-1 ml-auto mr-3 text-white bg-ptsgreen-dark rounded-full hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-ptsgreen-dark focus:ring-white'
                        >
                          <span className='sr-only'>Message Center</span>
                          <ChatAltIcon className='w-6 h-6' aria-hidden='true' />
                        </button>
                      </NavLink>
                      <button
                        type='button'
                        className='flex-shrink-0 p-1 ml-auto text-white bg-ptsgreen-dark rounded-full hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-ptsgreen-dark focus:ring-white'
                      >
                        <span className='sr-only'>View notifications</span>
                        <BellIcon className='w-6 h-6' aria-hidden='true' />
                      </button>
                    </div>
                  </div>
                  <div className='px-2 mt-3 space-y-1'>
                    {userNavigation.map((item) => (
                      <React.Fragment key={item.name}>
                        {item.action && (
                          <button
                            onClick={item.action}
                            className='block w-full px-3 py-2 font-medium text-left bg-transparent text-white border-none rounded'
                            style={{ fontSize: '13px' }}
                          >
                            {item.name}
                          </button>
                        )}

                        {!item.action && (
                          <Link
                            key={item.name}
                            to={item.to}
                            className='block px-3 py-2 font-medium no-underline bg-transparent text-white border-none rounded'
                            style={{ fontSize: '13px' }}
                          >
                            {item.name}
                          </Link>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>

        <header className='mb-4 bg-white shadow print:hidden'>
          <Header />
        </header>
        <main className='flex flex-col px-4 mx-auto mb-20 max-w-7xl sm:px-6 lg:px-8 print:max-w-none print:mx-0 print:px-0'>
          {children}
        </main>
      </div>
    </Fragment>
  );
};

export default Layout;
